import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, FlaskConical, ChevronRight, Package, Truck, CheckCircle2, RotateCcw } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import GlobalNavigationMenu from '../components/GlobalNavigationMenu';
import { isHomePathForRole } from '../utils/roleHome';

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem('authToken');

const STATUS_META = {
  prepared:        { label: 'Prepared',    color: 'bg-sky-100 text-sky-700',       icon: Package },
  dispatched:      { label: 'Dispatched',  color: 'bg-amber-100 text-amber-700',   icon: Truck },
  results_entered: { label: 'Results In',  color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
};

const LAB_COLORS = {
  GIA: 'bg-emerald-600', IGI: 'bg-blue-500', HRD: 'bg-purple-500', SGL: 'bg-orange-500',
};

export default function LabBatches() {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('all');

  // Hide the back arrow when this IS the user's home page — otherwise
  // clicking back lands them on a blank previous-history state.
  const showBack = !isHomePathForRole(location.pathname, currentUser?.role);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/lab-batches/`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setBatches(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = tab === 'all' ? batches : batches.filter(b => b.status === tab);

  const counts = {
    all:            batches.length,
    prepared:       batches.filter(b => b.status === 'prepared').length,
    dispatched:     batches.filter(b => b.status === 'dispatched').length,
    results_entered:batches.filter(b => b.status === 'results_entered').length,
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50 max-w-md mx-auto">
      {/* Header */}
      <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 shrink-0">
        {showBack ? (
          <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-emerald-600" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
        ) : (
          <div className="w-9 h-9 flex items-center justify-center shrink-0">
            <FlaskConical className="w-5 h-5 text-emerald-200" />
          </div>
        )}
        <div className="flex-1">
          <p className="text-xs text-emerald-200 font-medium">Production</p>
          <p className="text-base font-bold">Lab Batches</p>
        </div>
        <GlobalNavigationMenu />
      </div>

      {/* Status filter tabs */}
      <div className="bg-emerald-800 text-white overflow-x-auto shrink-0">
        <div className="flex">
          {[
            { key: 'all',            label: 'All' },
            { key: 'prepared',       label: 'Prepared' },
            { key: 'dispatched',     label: 'Dispatched' },
            { key: 'results_entered',label: 'Done' },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => setTab(s.key)}
              className={`flex-shrink-0 px-4 py-2 text-xs font-bold transition-all ${
                tab === s.key ? 'border-b-2 border-white text-white' : 'text-emerald-300'
              }`}
              data-testid={`tab-${s.key}`}
            >
              {s.label}
              {counts[s.key] > 0 && (
                <span className="ml-1.5 bg-white/20 px-1.5 py-0.5 rounded-full text-[10px]">
                  {counts[s.key]}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto px-4 pt-4">
        {loading ? (
          <div className="text-center py-12 text-gray-400">
            <RotateCcw className="w-8 h-8 mx-auto mb-2 animate-spin opacity-40" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <FlaskConical className="w-12 h-12 mx-auto mb-3 text-gray-200" />
            <p className="text-gray-500 font-semibold">No batches</p>
            <p className="text-sm text-gray-400 mt-1">Select verified tags in Diamond Verification to create one</p>
          </div>
        ) : (
          filtered.map(b => {
            const sm = STATUS_META[b.status] || STATUS_META.prepared;
            const StatusIcon = sm.icon;
            return (
              <button
                key={b.id}
                onClick={() => navigate(`/lab-batches/${b.id}`)}
                data-testid={`batch-card-${b.batch_number}`}
                className="w-full mb-3 bg-white rounded-2xl shadow-sm border border-gray-100 p-4 text-left active:bg-gray-50"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] px-2.5 py-0.5 rounded-full text-white font-bold ${LAB_COLORS[b.lab] || 'bg-gray-500'}`}>
                        {b.lab}
                      </span>
                      <span className="text-base font-bold text-gray-900 font-mono">{b.batch_number}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Assigned to <span className="font-semibold text-gray-700">{b.assigned_to_name}</span>
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <span className={`text-[10px] px-2.5 py-0.5 rounded-full font-bold ${sm.color}`}>
                      <StatusIcon className="w-2.5 h-2.5 inline mr-0.5" />
                      {sm.label}
                    </span>
                    <p className="text-[10px] text-gray-400">
                      {b.created_at ? new Date(b.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: '2-digit' }) : ''}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="font-bold text-gray-800">{(b.tag_ids || []).length}</span>
                    <span>{(b.tag_ids || []).length === 1 ? 'tag' : 'tags'}</span>
                  </div>
                  <div className="flex items-center gap-1 text-xs text-gray-500 ml-auto">
                    <span className="text-gray-400">By</span>
                    <span className="font-semibold text-gray-600">{b.created_by_name}</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-gray-300" />
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
